import { useContext, useState, useEffect } from 'react'
import Image from 'next/image'
import toast from 'react-hot-toast'
import axios from 'axios'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import getConfig from 'next/config'

import Container from '/src/components/@common.blocks/Container'
import Title from '/src/components/@common.blocks/Title'
import Text from '/src/components/@common.blocks/Text'
import Tabs, { TabPane } from '/src/components/@common.blocks/Tabs'
import Grid from '/src/components/@common.blocks/Grid'
import Button from '/src/components/@common.blocks/Button'
import { cutAddress } from '/src/helpers/formatter'
import { CoreApi } from '/src/helpers/CoreApi'
import EthereumContext from '/src/hooks/useEthereum'
import SessionContext from '/src/hooks/useSession'
import { getSession } from '/src/helpers/auth'

import rariscanLogo from '/public/logo-main.svg'
import bannerImage from '/public/images/rabbitzzNFT/banner.jpeg'
import logoImage from '/public/images/rabbitzzNFT/rabbitzz-logo.png'
import successIcon from '/public/icons/success.png'
import websiteIcon from '/public/icons/website.svg'
import twitterIcon from '/public/icons/twitter.svg'

const { publicRuntimeConfig } = getConfig()
const socialLinks = publicRuntimeConfig.socialLinks || {}

const NFTWhitelist = ({ collection }) => {
  const [joined, setJoined] = useState(false)
  const [participants, setParticipants] = useState(0)
  const { connectWallet, personalSign, detectProvider, account } = useContext(
    EthereumContext
  )
  const { user, setUser } = useContext(SessionContext)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const fetchWhitelistData = async () => {
    const session = await getSession()
    if (session && session.accessToken) {
      const { count } = await CoreApi.getTotalByAddressWS(session.accessToken)
      if (count > 0) return true
    }
    return false
  }

  const refreshParticipants = async () => {
    CoreApi.getTotalParticipantsWS()
      .then((result) => {
        setParticipants(result.totalCount)
      })
      .catch((error) => {
        console.log(error)
      })
  }

  useEffect(() => {
    refreshParticipants()
  }, [])

  useEffect(() => {
    if (user && user.address) {
      fetchWhitelistData()
        .then((result) => {
          setJoined(result)
        })
        .catch((error) => {
          console.log(error)
          setJoined(false)
        })
    } else {
      setJoined(false)
    }
  }, [user])

  const loginHandler = async (walletType) => {
    try {
      const publicAddress = account ? account : await connectWallet(walletType)
      if (!publicAddress) return

      const { provider } = (await detectProvider(walletType)) || {}
      if (!provider) throw new Error('Wallet not detected')

      const { msgToSign } = await CoreApi.walletConnect(publicAddress)
      const signature = await personalSign(publicAddress, msgToSign, provider)

      await axios.post('/api/v1/auth/login', {
        address: publicAddress,
        signature,
      })
      setUser({ address: publicAddress })
    } catch (error) {
      console.log(error)
      toast.error(error.message)
    }
  }

  const onRegister = async () => {
    try {
      const session = await getSession()
      const { msgToSign } = await CoreApi.getMessageForSignWS(
        session.accessToken
      )
      const signature = await personalSign(session.address, msgToSign)
      const recaptchaResponse = await executeRecaptcha()
      const { success } = await CoreApi.joinWhitelist(session.accessToken, {
        recaptchaResponse,
        signature,
      })
      if (success === true) {
        toast.success(
          'Your wallet has been successfully added to whitelist raffle!'
        )
        setJoined(true)
        refreshParticipants()
      }
    } catch (error) {
      console.log(error)
      const message = error?.response?.data || error.message
      toast.error(message)
    }
  }

  const isConnected = user && user.address
  return (
    <div className='nft-whitelist'>
      <Container className='nft-whitelist__container'>
        <div className='nft-whitelist__preface'>
          <div className='nft-whitelist__cover'>
            <Image
              src={bannerImage}
              layout='fill'
              alt={collection.name}
              className='nft-whitelist__cover-img'
            />
          </div>
          <div className='nft-whitelist__logo'>
            <Image src={logoImage} layout='fill' alt={collection.name} />
          </div>
          <Container>
            <Title level={1} className='nft-whitelist__title'>
              {collection.name}
            </Title>
            <Text size='lg' className='nft-whitelist__text text-center'>
              Rabbitzz Keys is a generative NFT collection creating by Rariscan
              team providing the holders a lifetime access to the Rariscan
              services. There will be a few categories of keys. More details to
              be announced soon. Follow us on Twitter to keep up with updates.
            </Text>
            <div className='nft-whitelist__socials'>
              <a
                className='nft-whitelist__socials-link'
                href='https://rariscan.io'
                target='_blank'
                rel='noreferrer'
              >
                <Image src={websiteIcon} width={18} height={18} alt='website' />
              </a>
              <a
                className='nft-whitelist__socials-link'
                href={socialLinks.twitter}
                target='_blank'
                rel='noreferrer'
              >
                <Image src={twitterIcon} width={18} height={18} alt='twitter' />
              </a>
            </div>
          </Container>
        </div>
      </Container>
      <Container className='nft-whitelist__container'>
        <Tabs
          defaultActiveKey='registration'
          onChange={(e) => console.log(e)}
          color='primary'
          className='nft-whitelist__tabs'
        >
          <TabPane
            tab={
              <>
                Participants — <b>{`${participants}`}</b>
              </>
            }
            key='registration'
          >
            <Grid gutter={2}>
              <Grid.Col xs={12} sm={6}>
                <Title level={2} className='nft-whitelist__subtitle'>
                  Get whitelisted
                </Title>
                <Text size='lg' className='nft-whitelist__text'>
                  Everyone can participate in the raffle for whitelist spots.
                  Connect your web3 wallet and join! It's free (no gas fee
                  required).
                </Text>
              </Grid.Col>
              <Grid.Col xs={12} sm={6}>
                {isConnected && !joined && (
                  <div className='nft-whitelist__connect nft-whitelist__connect--online'>
                    <div>
                      <Text size='md' weight='medium'>
                        {`Connected ${cutAddress(user.address, 5)}`}
                      </Text>
                    </div>
                    <Button
                      className='nft-whitelist__connect-btn'
                      onClick={onRegister}
                    >
                      Join the whitelist raffle
                    </Button>
                  </div>
                )}
                {!isConnected && !joined && (
                  <div className='nft-whitelist__connect nft-whitelist__connect--offline'>
                    <div>
                      <Text size='md' weight='medium' component='span'>
                        Wallet not connected
                      </Text>
                    </div>
                    <Button
                      className='nft-whitelist__connect-btn'
                      onClick={() => loginHandler('metamask')}
                    >
                      Connect the wallet
                    </Button>
                  </div>
                )}
                {joined && (
                  <div className='nft-whitelist__connect'>
                    <div className='nft-whitelist__connect-status'>
                      <div>
                        <Image
                          src={successIcon}
                          alt='success'
                          width={24}
                          height={24}
                        />
                      </div>
                      <Text size='md' className='text-center'>
                        Congratulations! Your address is participating in the
                        whitelist raffle
                      </Text>
                    </div>
                  </div>
                )}
              </Grid.Col>
            </Grid>
          </TabPane>
        </Tabs>
        <footer className='nft-whitelist__footer'>
          <div className='nft-whitelist__powered'>
            <Image src={rariscanLogo} alt='rariscan' width={17} height={16} />
            <Text weight='medium' component='span'>
              Powered by Rariscan
            </Text>
          </div>
          <Text component='span' size='md' weight='medium' color='light-gray'>
            support@rariscan.io
          </Text>
        </footer>
      </Container>
    </div>
  )
}

export default NFTWhitelist
